import React from 'react';
import { Link } from 'react-router-dom';
import { LogOut } from 'lucide-react';

/**
 * Top-of-page admin sub-nav. Shows the same set of tabs across every admin
 * page so users can pivot between Bonfire / Donations / Users / Profile
 * without losing context. Tabs are role-aware:
 *   - donations: see Donations + Profile
 *   - superadmin: see everything
 */
const ALL_TABS = [
  { key: 'home',      label: 'Dashboard',         path: '/admin',                 roles: ['superadmin', 'donations'] },
  { key: 'bonfire',   label: 'Bonfire List',     path: '/admin/bonfire-signups', roles: ['superadmin'] },
  { key: 'contact',   label: 'Contact Inbox',    path: '/admin/contact-enquiries', roles: ['superadmin', 'donations'] },
  { key: 'donations', label: 'Donation Requests', path: '/admin/donation-requests', roles: ['superadmin', 'donations'] },
  { key: 'sweet-jar', label: 'Sweet Jar',         path: '/admin/sweet-jar',       roles: ['superadmin', 'donations'] },
  { key: 'users',     label: 'Users',             path: '/admin/users',           roles: ['superadmin'] },
  { key: 'profile',   label: 'My Profile',        path: '/admin/profile',         roles: ['superadmin', 'donations'] },
];

export default function AdminTabs({ current, me, onLogout }) {
  const visible = ALL_TABS.filter((t) => t.roles.includes(me?.role));
  return (
    <div className="bg-[#0A0A0A] border-b border-zinc-800 sticky top-20 sm:top-24 z-30 mt-20 sm:mt-24" data-testid="admin-tabs">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row sm:items-center gap-3 py-3">
        <nav className="flex flex-wrap gap-2 flex-1">
          {visible.map((tab) => (
            <Link
              key={tab.key}
              to={tab.path}
              data-testid={`admin-tab-${tab.key}`}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                current === tab.key
                  ? 'bg-[#F0A500] text-[#0A0A0A]'
                  : 'bg-zinc-900 text-zinc-300 hover:bg-zinc-800 hover:text-white'
              }`}
            >
              {tab.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-3 text-xs text-zinc-400">
          <span data-testid="admin-tabs-username" className="hidden sm:inline truncate max-w-[200px]">
            {me?.username} <span className="text-zinc-600">·</span> <span className="uppercase tracking-wider">{me?.role}</span>
          </span>
          <button
            type="button"
            onClick={onLogout}
            data-testid="admin-tabs-logout"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-semibold transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
